import React from 'react';
import { Box, Card, CardContent, Typography, Tooltip as MuiTooltip, keyframes } from '@mui/material';
import {
  Star as StarIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CustomerRatingsOverall } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

// Largura fixa da coluna do gauge — garante que o SVG + labels tenham espaço
// suficiente sem depender de flex-grow, evitando overflow em containers
// apertados (ex: quando o card divide a linha com a tabela de divergência).
const GAUGE_COLUMN_WIDTH = 170;
const GAUGE_RADIUS = 52;
const GAUGE_STROKE = 11;
const GAUGE_SIZE = (GAUGE_RADIUS + GAUGE_STROKE) * 2 + 4;

interface Props {
  overall: CustomerRatingsOverall | null;
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 320,
    }}
  />
);

// ─── Gauge ─────────────────────────────────────────────────────────────────
interface GaugeProps {
  label: string;
  sublabel: string;
  value: number | null;
  color: string;
  accent: string;
  icon: React.ReactNode;
}

const Gauge: React.FC<GaugeProps> = ({ label, sublabel, value, color, accent, icon }) => {
  const CX = GAUGE_SIZE / 2;
  const CY = GAUGE_SIZE / 2;
  const circ = 2 * Math.PI * GAUGE_RADIUS;
  const pct = value != null ? (value / 5) * 100 : 0;
  const offset = circ * (1 - pct / 100);
  const display = value != null ? (Math.floor(value * 10) / 10).toFixed(1) : '—';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: GAUGE_COLUMN_WIDTH,
        flexShrink: 0,
      }}
    >
      {/* Label with icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.75 }}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.5,
            background: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 2px 8px ${color}40`,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Gauge circle */}
      <Box sx={{ position: 'relative', flexShrink: 0, width: GAUGE_SIZE, height: GAUGE_SIZE }}>
        <svg width={GAUGE_SIZE} height={GAUGE_SIZE}>
          <circle cx={CX} cy={CY} r={GAUGE_RADIUS} fill="none" stroke="#f1f5f9" strokeWidth={GAUGE_STROKE} />
          <motion.circle
            cx={CX} cy={CY} r={GAUGE_RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={GAUGE_STROKE}
            strokeLinecap="round"
            strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: value != null ? offset : circ }}
            transition={{ duration: 1.0, ease: EASE_OUT }}
            transform={`rotate(-90 ${CX} ${CY})`}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 800, color, lineHeight: 1, fontSize: '2.05rem' }}>
            {display}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', mt: 0.4, letterSpacing: 0.3 }}>
            de 5
          </Typography>
        </Box>
      </Box>

      {/* Sublabel */}
      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
          mt: 1.75,
          textAlign: 'center',
          fontSize: '0.78rem',
          lineHeight: 1.4,
          px: 1,
          minHeight: 36, // reserva espaço para 2 linhas
        }}
      >
        {sublabel}
      </Typography>
    </Box>
  );
};

// ─── Divergence insight generator (regras simples, em pt-BR) ───────────────
const getDivergenceInsight = (divergence: number | null, hasSentiment: boolean) => {
  if (divergence == null || !hasSentiment) {
    return {
      color: '#94a3b8',
      icon: '⚫',
      title: 'Sem dados suficientes',
      text: 'Aguarde mais avaliações com comentário para comparar nota e leitura da IA.',
    };
  }

  if (Math.abs(divergence) < 0.4) {
    return {
      color: '#22c55e',
      icon: '🟢',
      title: 'Alinhado',
      text: 'A nota do cliente e o que a IA lê nos comentários estão alinhados — sinal consistente.',
    };
  }

  if (divergence > 0.8) {
    return {
      color: '#ef4444',
      icon: '⚠️',
      title: 'Risco silencioso',
      text: `Clientes estão dando nota boa (${divergence.toFixed(1)} ponto${Math.abs(divergence) > 1.05 ? 's' : ''} acima) mas o que escrevem mostra mais problemas. Vale investigar.`,
    };
  }

  if (divergence > 0.4) {
    return {
      color: '#f59e0b',
      icon: '🟡',
      title: 'Atenção',
      text: `Nota do cliente levemente acima da leitura da IA (${divergence.toFixed(1)}). Provavelmente educação nas estrelas — monitore.`,
    };
  }

  if (divergence < -0.8) {
    return {
      color: '#0ea5e9',
      icon: 'ℹ️',
      title: 'Clientes duros',
      text: `O que os clientes escrevem é mais leve do que a nota (${Math.abs(divergence).toFixed(1)} ponto${Math.abs(divergence) > 1.05 ? 's' : ''} abaixo). Podem estar sendo severos nas estrelas por um detalhe específico.`,
    };
  }

  return {
    color: '#f59e0b',
    icon: '🟡',
    title: 'Leve divergência',
    text: `Diferença de ${Math.abs(divergence).toFixed(1)} ponto entre nota e sentimento. Mantenha de olho.`,
  };
};

// ─── Main component ────────────────────────────────────────────────────────
const DualScoreCard: React.FC<Props> = ({ overall, loading }) => {
  if (loading) return <SkeletonCard />;

  const o = overall ?? {
    avgStationRating: null,
    avgAttendantRating: null,
    avgSentiment: null,
    totalRatings: 0,
    totalComments: 0,
    divergence: null,
  };

  // Nota do cliente combinada: média de station + attendant (simples, intuitivo)
  const customerScore = (() => {
    const parts = [o.avgStationRating, o.avgAttendantRating].filter((v): v is number => v != null);
    if (parts.length === 0) return null;
    return parts.reduce((a, b) => a + b, 0) / parts.length;
  })();

  // Recalcula a divergência considerando a nota combinada
  const adjustedDivergence = (customerScore != null && o.avgSentiment != null)
    ? Math.round((customerScore - o.avgSentiment) * 100) / 100
    : null;

  const insight = getDivergenceInsight(adjustedDivergence, o.avgSentiment != null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(120,105,223,0.10)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ pb: '20px !important' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
              Nota do Cliente × Leitura da IA
            </Typography>
            <MuiTooltip title="Total de avaliações no período" arrow placement="top">
              <Typography
                variant="body2"
                sx={{ color: '#64748b', cursor: 'default', borderBottom: '1px dashed #cbd5e1' }}
              >
                {o.totalRatings.toLocaleString('pt-BR')} avaliações
              </Typography>
            </MuiTooltip>
          </Box>

          {/* Two gauges centered — fixed widths + flexWrap as safety net */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: { xs: 2, md: 3 },
              mb: 3,
              flexWrap: 'wrap',
            }}
          >
            <Gauge
              label="Nota do Cliente"
              sublabel="Média das estrelas (posto + frentista)"
              value={customerScore}
              color="#7869DF"
              accent="linear-gradient(135deg, #667eea, #7869DF)"
              icon={<StarIcon sx={{ color: '#fff', fontSize: 16 }} />}
            />
            <Gauge
              label="Leitura da IA"
              sublabel={`Sentimento inferido de ${o.totalComments.toLocaleString('pt-BR')} comentários`}
              value={o.avgSentiment}
              color="#0ea5e9"
              accent="linear-gradient(135deg, #0ea5e9, #6366f1)"
              icon={<SparkleIcon sx={{ color: '#fff', fontSize: 16 }} />}
            />
          </Box>

          {/* Divergence insight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.3, ease: EASE_OUT }}
          >
            <Box
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${insight.color}40`,
                bgcolor: `${insight.color}10`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
              }}
            >
              <Typography sx={{ fontSize: '1.2rem', lineHeight: 1, flexShrink: 0, mt: '1px' }}>
                {insight.icon}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: insight.color,
                    fontSize: '0.92rem',
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {insight.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.87rem', lineHeight: 1.45 }}>
                  {insight.text}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default DualScoreCard;
